
import { useState } from 'react';
import { useScrollAnimation, getAnimationClass } from '../../../hooks/useScrollAnimation';

function AnimatedSection({ children, animation = 'slide-up' as 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in', className = '' }: { children: React.ReactNode; animation?: 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in'; className?: string }) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div ref={ref} className={`${getAnimationClass(isVisible, animation)} ${className}`}>
      {children}
    </div>
  );
}

export default function Features() {
  const [activeCategory, setActiveCategory] = useState(0);

  const categories = [
    { label: 'Gestión Clínica', icon: 'ri-stethoscope-line' },
    { label: 'Diagnóstico', icon: 'ri-search-eye-line' },
    { label: 'Automatización', icon: 'ri-robot-line' },
  ];

  const featureGroups = [
    [
      { icon: 'ri-file-list-3-line', title: 'Historiales Digitales', description: 'Expedientes clínicos oftalmológicos completos con acceso instantáneo desde cualquier dispositivo.' },
      { icon: 'ri-calendar-check-line', title: 'Agenda Inteligente', description: 'Programación de citas con recordatorios automáticos y gestión de disponibilidad en tiempo real.' },
      { icon: 'ri-user-heart-line', title: 'Ficha del Paciente', description: 'Perfil completo con historial de visitas, tratamientos, recetas y evolución visual documentada.' },
      { icon: 'ri-capsule-line', title: 'Recetas y Prescripciones', description: 'Generación digital de recetas ópticas y farmacológicas con firma electrónica integrada.' },
      { icon: 'ri-money-dollar-circle-line', title: 'Facturación y Cobros', description: 'Control de pagos, seguros médicos y facturación electrónica automatizada para tu consulta.' },
      { icon: 'ri-glasses-line', title: 'Módulo de Óptica Integrado', description: 'Gestiona monturas, lentes, inventario, proveedores y ventas de tu óptica desde el mismo panel clínico.' },
    ],
    [
      { icon: 'ri-eye-line', title: 'Agudeza Visual', description: 'Registro preciso de agudeza visual con escalas Snellen, LogMAR y notación decimal automatizada.' },
      { icon: 'ri-contrast-line', title: 'Presión Intraocular', description: 'Seguimiento de PIO con gráficos de tendencia y alertas automáticas por valores anormales.' },
      { icon: 'ri-scan-line', title: 'Fondo de Ojo', description: 'Documentación detallada de hallazgos en retina con esquemas anatómicos interactivos.' },
      { icon: 'ri-focus-3-line', title: 'Refracción', description: 'Registro completo de refracción objetiva y subjetiva con cálculo automático de lentes.' },
      { icon: 'ri-image-line', title: 'Imágenes Diagnósticas', description: 'Almacenamiento y comparación de OCT, campimetrías, topografías y fotografías clínicas.' },
      { icon: 'ri-bar-chart-grouped-line', title: 'Análisis Comparativo', description: 'Compara evolución entre visitas con gráficos interactivos y reportes de progresión.' },
    ],
    [
      { icon: 'ri-notification-3-line', title: 'Recordatorios Automáticos', description: 'Envío de SMS y email para citas, controles periódicos y seguimiento post-operatorio.' },
      { icon: 'ri-mail-send-line', title: 'Comunicación Masiva', description: 'Campañas de salud visual, recordatorios de control anual y promociones para pacientes.' },
      { icon: 'ri-line-chart-line', title: 'Reportes Automáticos', description: 'Generación automática de informes clínicos, estadísticas de consulta y métricas de rendimiento.' },
      { icon: 'ri-shield-check-line', title: 'Verificación de Seguros', description: 'Validación automática de cobertura de seguros y autorización previa de procedimientos.' },
      { icon: 'ri-cloud-line', title: 'Respaldos en la Nube', description: 'Copias de seguridad automáticas con encriptación de grado militar para todos tus datos.' },
      { icon: 'ri-links-line', title: 'Integraciones', description: 'Conexión con equipos oftalmológicos, laboratorios ópticos y sistemas de salud existentes.' },
    ],
  ];

  return (
    <section id="funcionalidades" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <AnimatedSection animation="fade-in">
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-semibold text-emerald-600 uppercase tracking-widest mb-3">Funcionalidades</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Todo lo que necesitas para tu <span className="font-display italic text-emerald-600">consulta oftalmológica</span>
            </h2>
            <p className="text-sm text-gray-500 max-w-xl mx-auto">
              Una suite completa de herramientas diseñadas por oftalmólogos para oftalmólogos. Desde la gestión clínica hasta el diagnóstico avanzado.
            </p>
          </div>
        </AnimatedSection>

        {/* Category Tabs */}
        <AnimatedSection animation="slide-up">
          <div className="flex items-center justify-center mb-12">
            <div className="inline-flex items-center bg-gray-100 rounded-full p-1">
              {categories.map((cat, index) => (
                <button
                  key={index}
                  onClick={() => setActiveCategory(index)}
                  className={`flex items-center space-x-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                    activeCategory === index
                      ? 'bg-white text-gray-900 shadow-md'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  <span>{cat.label}</span>
                </button>
              ))}
            </div>
          </div>
        </AnimatedSection>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featureGroups[activeCategory].map((feature, index) => (
            <AnimatedSection key={`${activeCategory}-${index}`} animation="scale-in">
              <div className="group p-6 rounded-xl border border-gray-100 hover:border-emerald-200 hover:shadow-lg transition-all cursor-pointer bg-white h-full">
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center mb-5 group-hover:bg-emerald-100 group-hover:scale-110 transition-all">
                  <i className={`${feature.icon} text-emerald-600 text-xl`}></i>
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{feature.description}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}
